import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { Locale } from "@/lib/i18n/config";
import type { SellerProductReviewView } from "@/lib/api/types";
import { computeReviewAggregates } from "@/modules/reviews/lib/review-display";
import { ReviewStars } from "@/modules/reviews/components/review-ui";

type ReviewsSummaryCardProps = {
  locale: Locale;
  /** Reviews currently loaded — the aggregate is a best-effort over these. */
  items: SellerProductReviewView[];
  /** Total review count across all pages (from the count query). */
  total: number;
  /** Published review count across all pages (from the count query). */
  publishedCount: number;
};

/**
 * Rating summary matching the Trioty design: big average, star distribution,
 * and two derived insight tiles. Everything is computed from data already on
 * hand — the average/distribution/positive share come from the loaded reviews,
 * and the published share from the count queries — so no backend work is needed.
 */
export function ReviewsSummaryCard({
  locale,
  items,
  total,
  publishedCount,
}: ReviewsSummaryCardProps) {
  const t = useTranslations();
  const { average, distribution } = computeReviewAggregates(items);
  const numberFormat = new Intl.NumberFormat(locale === "ar" ? "ar-EG" : "en-US");
  const percentFormat = new Intl.NumberFormat(locale === "ar" ? "ar-EG" : "en-US", {
    style: "percent",
    maximumFractionDigits: 0,
  });
  const averageText = average === null ? "—" : average.toFixed(1);
  const totalText = numberFormat.format(total);
  // % of loaded reviews rated 4★ or higher.
  const positiveText =
    items.length === 0
      ? "—"
      : percentFormat.format(
          items.filter((review) => review.rating >= 4).length / items.length,
        );
  // % of all reviews currently published/visible (from count queries).
  const publishedText =
    total === 0 ? "—" : percentFormat.format(publishedCount / total);

  const divider = (
    <div
      aria-hidden="true"
      className="hidden h-[92px] w-px shrink-0 bg-[color:var(--line2)] lg:block"
    />
  );

  return (
    <div className="rounded-[20px] border border-[color:var(--line2)] bg-[color:var(--card)] p-2">
      <div className="flex flex-col items-stretch gap-5 rounded-[15px] border border-[color:var(--line2)] bg-[color:var(--surface-alt)] px-6 py-5 lg:grid lg:[grid-template-columns:auto_1px_minmax(0,1fr)_1px_auto] lg:items-center lg:gap-[26px]">
        {/* Average */}
        <div className="px-3 text-center">
          <div className="text-[50px] font-extrabold leading-none tracking-[-0.02em] text-[color:var(--text-primary)]">
            <bdi>{averageText}</bdi>
          </div>
          <div className="mb-[7px] mt-[11px] flex justify-center">
            <ReviewStars value={average ?? 0} size={18} />
          </div>
          <div className="text-[12.5px] font-medium text-[color:var(--text-muted)]">
            {t(keys.reviews.totalLabel, { count: totalText })}
          </div>
        </div>

        {divider}

        {/* Distribution */}
        <div className="flex min-w-0 flex-col gap-[9px]">
          {distribution.map((row) => (
            <div key={row.stars} className="flex items-center gap-3">
              <span className="inline-flex w-[30px] shrink-0 items-center gap-1 text-[12.5px] font-semibold text-[color:var(--text-secondary)]">
                {row.stars}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="#F5A623" stroke="none" aria-hidden="true">
                  <path d="m12 2 2.9 6.3 6.9.6-5.2 4.6 1.6 6.8L12 17.3 5.8 20.9l1.6-6.8L2.2 8.9l6.9-.6L12 2Z" />
                </svg>
              </span>
              <span className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-[color:var(--track)]">
                <span
                  className="block h-full rounded-full bg-[#F5A623]"
                  style={{ width: `${row.pct}%` }}
                />
              </span>
              <span className="w-9 shrink-0 text-end text-[12px] font-semibold text-[color:var(--text-muted)]">
                {row.pct}%
              </span>
            </div>
          ))}
        </div>

        {divider}

        {/* Derived insight tiles — computed from data already loaded. */}
        <div className="flex flex-row justify-center gap-8 px-2 lg:flex-col lg:gap-4">
          <div className="text-center">
            <div className="text-[23px] font-bold leading-none text-[color:var(--success-500)]">
              {positiveText}
            </div>
            <div className="mt-[5px] text-[11.5px] font-medium text-[color:var(--text-muted)]">
              {t(keys.reviews.summaryPositive)}
            </div>
          </div>
          <div className="text-center">
            <div className="text-[23px] font-bold leading-none text-[color:var(--text-primary)]">
              {publishedText}
            </div>
            <div className="mt-[5px] text-[11.5px] font-medium text-[color:var(--text-muted)]">
              {t(keys.reviews.summaryPublished)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
